import type React from 'react';

import { Box, Divider, FieldSet, FormField, Input, Label, Legend } from '@zillow/constellation-10';

import FixedOrVariableSelector from '../Utilities/FixedOrVariableSelector';
import HeaderToggle from '../Utilities/HeaderToggle';
import type { Section } from '../../../../models/index';
import { NATURAL_GAS, UTILITIES_INFORMATION } from '../../../../constants';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import RadioListBox from '../../../Widgets/RadioListBox';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';
import PhoneInput from '../../../Widgets/PhoneInput';

const INITIAL_STATE = {
  [NATURAL_GAS.FIELD_NAME.IS_ENABLED]: true,
  [NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: false,
  [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
  [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: false,
  [NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
  [UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_PHONE_NUMBER]: null,
  [UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_SERVICE_PROVIDER]: null,
  [NATURAL_GAS.FIELD_NAME.SERVICE_PROVIDER]: null,
  [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
  [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
};

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function NaturalGas_v4({ section, setSectionValues }: Props): JSX.Element {
  const option1 = NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY;
  const option2 = NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY;
  const naturalGas_isEnabled = section.getFieldValue(NATURAL_GAS.FIELD_NAME.IS_ENABLED);
  const naturalGas_tenantPaysInAdditionToRent = section.getFieldValue(
    NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );
  const option2Selected = section.getFieldValue(option2);

  function setAdditionalFixedAmountValue(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  function setAdditionalFixedAmountSelected(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED]: true,
      [NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
    });
  }

  function setServiceProvider(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [NATURAL_GAS.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(NATURAL_GAS.FIELD_NAME.IS_ENABLED),
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        NATURAL_GAS.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: false,
      [NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED]: false,
      [NATURAL_GAS.FIELD_NAME.SERVICE_PROVIDER]: '',
      [NATURAL_GAS.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: '',
    });
  }

  function setAdditionalVariableCostSelected(): void {
    setSectionValues({
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE]: 0,
      [NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED]: false,
      [NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED]: true,
    });
  }

  function renderOption2Details(): React.ReactNode {
    return (
      <Box css={{ marginTop: 'layout.tight' }}>
        <FixedOrVariableSelector
          legendText="What is the additional fee for natural gas?"
          fixedAmountSelected={section.getFieldValue(NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_SELECTED)}
          fixedAmountValue={section.getFieldValue(NATURAL_GAS.FIELD_NAME.FIXED_AMOUNT_VALUE)}
          setFixedAmountSelected={setAdditionalFixedAmountSelected}
          setFixedAmountValue={setAdditionalFixedAmountValue}
          setVariableMonthlySelected={setAdditionalVariableCostSelected}
          variableMonthlyCostSelected={section.getFieldValue(NATURAL_GAS.FIELD_NAME.VARIABLE_COST_SELECTED)}
        />
      </Box>
    );
  }

  function onServiceProviderPhoneNumberChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_PHONE_NUMBER]: value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Natural gas"
        subheader="Applies to this property"
        switchOn={naturalGas_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {naturalGas_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>What service provider?</Label>}
            control={
              <Input
                value={section.getFieldValue(UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_SERVICE_PROVIDER)}
                onChange={setServiceProvider}
              />
            }
          />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>What is the service provider’s number?</Label>}
            control={
              <PhoneInput
                value={section.getFieldValue(UTILITIES_INFORMATION.FIELD_NAME.NATURAL_GAS_PHONE_NUMBER)}
                onChange={onServiceProviderPhoneNumberChange}
              />
            }
          />
          <SwitchWithTitle
            text="Tenant pays in addition to rent"
            onClick={togglePaysInAddition}
            toggled={naturalGas_tenantPaysInAdditionToRent}
          />
          {naturalGas_tenantPaysInAdditionToRent && (
            <Box css={{ marginTop: 'layout.tight' }}>
              {
                <>
                  <FieldSet required legend={<Legend>Who will be responsible for natural gas set up?</Legend>}>
                    <RadioListBox
                      section={section}
                      setSectionValues={setSectionValues}
                      option1FieldName={option1}
                      option2FieldName={option2}
                      option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                      option1Content={UTILITIES_HEADING_AND_CONTENT.option1Content}
                      option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                      option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
                    />
                  </FieldSet>
                  {option2Selected && renderOption2Details()}
                </>
              }
            </Box>
          )}
        </>
      )}
    </>
  );
}

NaturalGas_v4.description = 'Move service provider up';
